// @core/stores/calendar/CalendarCalendarStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { calendarApi } from '@core/api';
import type {
  Calendar,
  CalendarLifecycleResponse,
  CancelEntityScheduleRequest,
  CheckEntityAvailabilityRequest,
  CheckEntityAvailabilityResponse,
  ListEntityScheduleResponse,
  RescheduleEntityRequest,
  ScheduleEntityRequest,
} from '@core/types/calendar';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class CalendarCalendarStore {
  // List state
  items: Calendar[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Calendar | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Calendar>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Calendar | undefined {
    return this.cache.get(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Cancel entity schedule
   */
  async CancelEntitySchedule(target_type: string, target_id: string, data: CancelEntityScheduleRequest): Promise<CalendarLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.CancelEntitySchedule(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Check entity availability
   */
  async CheckEntityAvailability(target_type: string, data: CheckEntityAvailabilityRequest): Promise<CheckEntityAvailabilityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.CheckEntityAvailability(target_type, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List entity schedule
   */
  async ListEntitySchedule(target_type: string, params?: PaginatedRequest & { time_max?: string | number | boolean; time_min?: string | number | boolean; }): Promise<ListEntityScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.ListEntitySchedule(target_type, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reschedule entity
   */
  async RescheduleEntity(target_type: string, target_id: string, data: RescheduleEntityRequest): Promise<CalendarLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.RescheduleEntity(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Schedule entity
   */
  async ScheduleEntity(target_type: string, target_id: string, data: ScheduleEntityRequest): Promise<CalendarLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.ScheduleEntity(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const calendarCalendarStore = new CalendarCalendarStore();
